import {
  contrato,
  montarCaminho,
  type ChaveRota,
  type EntradaDe,
  type ErroApi,
  type RespostaDe,
  type SemEntrada,
} from "@noesis/shared";

/**
 * Cliente da API tipado a partir do contrato compartilhado.
 *
 * Não existe URL escrita à mão em componente nenhum: chamar `pedir("x")` com
 * uma chave que o contrato não conhece é erro de compilação, e o corpo e a
 * resposta já vêm com o tipo certo. É o outro lado do invariante 2.
 */

/**
 * A interceptação da trava de senha, num ponto só.
 *
 * A conta com `deve_redefinir_senha` ligado leva 403 `senha_a_redefinir` em
 * **toda** rota autenticada menos três. Decidir o que fazer com isso na tela
 * inicial não bastaria: quem cola link direto para `/caderno` nunca passa por
 * ela, e cairia numa tela quebrada. Aqui a decisão atravessa toda chamada,
 * porque toda chamada passa por `pedir`.
 *
 * O aviso é disparado **e** o erro continua sendo lançado: quem chamou ainda
 * precisa parar o que estava fazendo. Quem redesenha é o `App`, que troca o
 * roteador inteiro pelo formulário — não há para onde navegar, então também
 * não há laço possível. E `sessao.redefinirSenha` atravessa a trava no
 * servidor: ela nunca devolve 403, então a própria tela não se re-dispara.
 */
type AvisoDeTrava = () => void;
let avisarTrava: AvisoDeTrava | null = null;

export function aoExigirNovaSenha(aviso: AvisoDeTrava | null): void {
  avisarTrava = aviso;
}

/**
 * O ensaio do tour (ADR 041): enquanto ele dura, os pedidos levam o cabeçalho
 * que faz o servidor responder de um banco de mentira, e nada chega à conta.
 *
 * Mora no `sessionStorage`, e não num estado do React, porque precisa valer
 * **desde o primeiro pedido** da página: entrar e sair do ensaio recarregam a
 * página inteira, e sem isso a lista de trilhas chegaria real antes de o tour
 * dizer que está no ensaio. É por aba: o celular aberto ao mesmo tempo continua
 * nos dados reais.
 */
const CHAVE_DO_ENSAIO = "grifo-ensaio";

function lerEnsaio(): boolean {
  try {
    return sessionStorage.getItem(CHAVE_DO_ENSAIO) === "1";
  } catch {
    return false;
  }
}

let ensaio = typeof window !== "undefined" && lerEnsaio();

export function emEnsaio(): boolean {
  return ensaio;
}

export function definirEnsaio(ligado: boolean): void {
  ensaio = ligado;
  try {
    if (ligado) sessionStorage.setItem(CHAVE_DO_ENSAIO, "1");
    else sessionStorage.removeItem(CHAVE_DO_ENSAIO);
  } catch {
    /* sem armazenamento: vale só até a página recarregar */
  }
}

export class FalhaDaApi extends Error {
  constructor(
    readonly codigo: ErroApi["erro"]["codigo"] | "rede",
    mensagem: string,
    readonly campos?: { caminho: string; mensagem: string }[],
    readonly status?: number,
  ) {
    super(mensagem);
    this.name = "FalhaDaApi";
  }
}

/**
 * `pedir("trilha.editar", { params: { id }, corpo: { ... } })`.
 *
 * As duas metades são nomeadas em vez de posicionais porque params e corpo são
 * ambos objetos: trocados de lugar, um par posicional compilaria em quase toda
 * rota e só quebraria em produção. Rota sem params e sem corpo não aceita
 * segundo argumento nenhum — `pedir("sessao.atual")` e pronto.
 */
export async function pedir<K extends ChaveRota>(
  chave: K,
  ...args: SemEntrada<K> extends true ? [] : [entrada: EntradaDe<K>]
): Promise<RespostaDe<K>> {
  const def = contrato[chave];

  /*
   * Leitura em voo é reaproveitada por dois segundos (Fase 6A).
   *
   * É o que faz o pré-carregamento valer alguma coisa: apontar o menu dispara a
   * consulta, e o `pedir` da tela que abre em seguida **pega a mesma promessa**
   * em vez de começar outra. Sem isto o pré-carregamento só esquentaria o cache
   * HTTP, que estas rotas não têm.
   *
   * Dois segundos, e só GET. A janela é o tempo entre apontar e clicar, não um
   * cache: mais do que isso e a tela passaria a mostrar dado velho depois de uma
   * resposta gravada. Rota com corpo nunca entra — ela muda estado.
   */
  if (def.metodo === "GET") {
    const args0 = args[0] as { params?: Record<string, string> } | undefined;
    const chaveDaVez = `${chave}:${JSON.stringify(args0?.params ?? null)}`;
    const emVoo = LEITURAS.get(chaveDaVez);
    if (emVoo && Date.now() - emVoo.em < JANELA_DE_VOO_MS) {
      return emVoo.promessa as Promise<RespostaDe<K>>;
    }
    const promessa = fazerPedido(chave, args[0] as EntradaDe<K> | undefined).finally(() => {
      // A entrada some assim que a resposta chega: o que ela guarda é a viagem,
      // não o resultado. Deixá-la viva depois disso seria um cache, e um cache
      // sem invalidação é o defeito que ele parece resolver.
      const atual = LEITURAS.get(chaveDaVez);
      if (atual?.promessa === promessa) LEITURAS.delete(chaveDaVez);
    });
    LEITURAS.set(chaveDaVez, { promessa: promessa as Promise<unknown>, em: Date.now() });
    return promessa;
  }

  return fazerPedido(chave, args[0] as EntradaDe<K> | undefined);
}

/** A janela em que uma leitura em voo é reaproveitada. */
const JANELA_DE_VOO_MS = 2000;

const LEITURAS = new Map<string, { promessa: Promise<unknown>; em: number }>();

/**
 * Dispara a leitura de uma rota **sem esperar por ela** — o pré-carregamento
 * do menu (Fase 6A).
 *
 * Falha é engolida de propósito: quem apontou o menu não pediu nada, e um erro
 * aqui não tem tela para aparecer. Se a navegação acontecer, o `pedir` da tela
 * refaz a consulta e mostra o erro no lugar certo.
 */
export function precarregar<K extends ChaveRota>(
  chave: K,
  ...args: SemEntrada<K> extends true ? [] : [entrada: EntradaDe<K>]
): void {
  if (contrato[chave].metodo !== "GET") return;
  void (pedir as (c: K, ...a: unknown[]) => Promise<unknown>)(chave, ...args).catch(() => undefined);
}

async function fazerPedido<K extends ChaveRota>(chave: K, entrada: EntradaDe<K> | undefined): Promise<RespostaDe<K>> {
  const def = contrato[chave];
  const dadosDaEntrada = entrada as { params?: Record<string, string>; corpo?: unknown } | undefined;
  const corpo = dadosDaEntrada?.corpo;
  // A interpolação mora no contrato, não aqui: é a mesma função que o teste
  // exercita e que qualquer outro consumidor usaria. URL escrita à mão em
  // componente continua não existindo (invariante 2).
  const caminho = montarCaminho(def.caminho, dadosDaEntrada?.params);

  let resposta: Response;
  try {
    resposta = await fetch(caminho, {
      method: def.metodo,
      // Mesma origem: o cookie de sessão vai junto sem configuração extra.
      credentials: "same-origin",
      headers: {
        ...(corpo === undefined ? {} : { "content-type": "application/json" }),
        ...(ensaio ? { "x-grifo-ensaio": "1" } : {}),
      },
      body: corpo === undefined ? undefined : JSON.stringify(corpo),
    });
  } catch {
    // Falha de rede não é 500: o app está offline ou o servidor caiu. A tela
    // precisa saber a diferença para escrever a frase certa.
    throw new FalhaDaApi("rede", "Sem conexão com o Grifo. Verifique a internet.");
  }

  if (!resposta.ok) {
    let corpoErro: Partial<ErroApi> = {};
    try {
      corpoErro = (await resposta.json()) as Partial<ErroApi>;
    } catch {
      /* resposta sem JSON: fica o genérico abaixo */
    }
    const e = corpoErro.erro;
    if (e?.codigo === "senha_a_redefinir") avisarTrava?.();
    throw new FalhaDaApi(
      e?.codigo ?? "erro_interno",
      e?.mensagem ?? "Não foi possível completar a ação.",
      e?.campos,
      resposta.status,
    );
  }

  const dados = (await resposta.json()) as unknown;
  // O contrato também valida o que chega. Um servidor mais novo que o front
  // falha aqui, com mensagem, em vez de espalhar `undefined` pela tela.
  const conferido = def.resposta.safeParse(dados);
  if (!conferido.success) {
    throw new FalhaDaApi("erro_interno", "O servidor respondeu fora do contrato. Recarregue a página.");
  }
  return conferido.data as RespostaDe<K>;
}
